/**
 * API utilities for making authenticated requests to the backend.
 *
 * This module provides helper functions that automatically include
 * authentication tokens in API requests.
 */

import type { useLogto } from '@logto/react';

// Type for the Logto hook return value
type LogtoHook = ReturnType<typeof useLogto>;

/**
 * Make an authenticated API request.
 *
 * @param url - API endpoint URL
 * @param logto - Logto context from useLogto hook
 * @param options - Fetch options (method, headers, body, etc.)
 * @returns Promise with the fetch Response
 */
export async function authenticatedFetch(
    url: string,
    logto: LogtoHook,
    options: RequestInit = {}
): Promise<Response> {
    const authHeaders: Record<string, string> = {};

    // Try to get access token first
    try {
        const accessToken = await logto.getAccessToken();
        if (accessToken) {
            authHeaders['Authorization'] = `Bearer ${accessToken}`;
        }
    } catch (error) {
        // getAccessToken() throws in Logto v4+ when no API resources are configured
        console.debug('[API] getAccessToken() failed, will try ID token claims fallback:', error);
    }

    // If no access token, try to get user ID from ID token claims as fallback
    if (!authHeaders['Authorization']) {
        try {
            const claims = await logto.getIdTokenClaims();
            if (claims?.sub) {
                // Pass user ID via X-User-Id header (backend supports this fallback)
                authHeaders['X-User-Id'] = claims.sub;
                console.debug('[API] Using X-User-Id header fallback with user:', claims.sub);
            }
        } catch (error) {
            console.error('[API] Failed to get ID token claims:', error);
        }
    }

    // If still no auth, check if user is authenticated
    if (!authHeaders['Authorization'] && !authHeaders['X-User-Id']) {
        const isAuthenticated = logto.isAuthenticated;
        if (!isAuthenticated) {
            throw new Error('User is not authenticated. Please sign in.');
        }
        // User is authenticated but we couldn't get tokens - this shouldn't happen
        console.warn('[API] User is authenticated but no tokens available');
    }

    // Merge headers with auth headers
    const headers = {
        ...options.headers,
        ...authHeaders,
    };

    // Make the request
    return fetch(url, {
        ...options,
        headers,
    });
}

/**
 * Make an authenticated GET request and return JSON.
 */
export async function apiGet<T = any>(
    url: string,
    logto: LogtoHook
): Promise<T> {
    const response = await authenticatedFetch(url, logto);

    if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
}

/**
 * Make an authenticated POST request with JSON body.
 */
export async function apiPost<T = any>(
    url: string,
    logto: LogtoHook,
    body?: any
): Promise<T> {
    const response = await authenticatedFetch(url, logto, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
}

/**
 * Make an authenticated DELETE request.
 */
export async function apiDelete<T = any>(
    url: string,
    logto: LogtoHook
): Promise<T> {
    const response = await authenticatedFetch(url, logto, {
        method: 'DELETE',
    });

    if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
}

/**
 * Upload a file with authentication.
 */
export async function apiUploadFile<T = any>(
    url: string,
    logto: LogtoHook,
    formData: FormData
): Promise<T> {
    const response = await authenticatedFetch(url, logto, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header for FormData - browser will set it with boundary
    });

    if (!response.ok) {
        throw new Error(`File upload failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
}
